export interface ResizeOptions {
  targetSizeKB?: number; // Target file size in KB (default: 800)
  maxSizeKB?: number; // Maximum allowed size in KB (default: 1000)
  maxDimension?: number; // Maximum width/height (default: 1920)
  quality?: number; // Initial quality (default: 0.9)
  onProgress?: (status: string) => void; // Progress callback
}

export interface ResizeResult {
  file: File;
  originalSize: number;
  finalSize: number;
  wasResized: boolean;
  compressionRatio: number;
}

/**
 * Resize and compress an image to target file size while maintaining quality
 */
export async function resizeImage(
  file: File,
  options: ResizeOptions = {}
): Promise<ResizeResult> {
  const {
    targetSizeKB = 800,
    maxSizeKB = 1000,
    maxDimension = 1920,
    quality = 0.9,
    onProgress
  } = options;

  const originalSize = file.size;
  const targetSizeBytes = targetSizeKB * 1024;
  const maxSizeBytes = maxSizeKB * 1024;

  // Always resize if file is close to or over the limit to ensure we stay under 1MB
  if (originalSize <= targetSizeBytes * 0.8) {
    return {
      file,
      originalSize,
      finalSize: originalSize,
      wasResized: false,
      compressionRatio: 1
    };
  }

  onProgress?.('Loading image...');

  // Load image
  const img = await loadImageFromFile(file);
  
  // Calculate initial dimensions
  let width = img.width;
  let height = img.height;
  
  // Scale down if too large
  if (width > maxDimension || height > maxDimension) {
    onProgress?.('Resizing dimensions...');
    if (width > height) {
      height = Math.round((height * maxDimension) / width);
      width = maxDimension;
    } else {
      width = Math.round((width * maxDimension) / height);
      height = maxDimension;
    }
  }

  // Try progressive compression - be more aggressive to ensure we stay under 1MB
  let currentQuality = quality;
  let attempts = 0;
  const maxAttempts = 8;
  
  while (attempts < maxAttempts) {
    onProgress?.(`Compressing (attempt ${attempts + 1}/${maxAttempts})...`);
    
    const compressed = await compressImage(img, width, height, currentQuality, file.type);
    
    // STRICT: Must be under max size, no exceptions
    if (compressed.size <= maxSizeBytes) {
      return {
        file: compressed,
        originalSize,
        finalSize: compressed.size,
        wasResized: true,
        compressionRatio: originalSize / compressed.size
      };
    }
    
    // Try more aggressive compression
    if (currentQuality > 0.6) {
      // Reduce quality aggressively
      currentQuality -= 0.15;
    } else if (currentQuality > 0.4) {
      currentQuality -= 0.1;
    } else {
      // Reduce dimensions by 20%
      width = Math.round(width * 0.8);
      height = Math.round(height * 0.8);
      currentQuality = 0.8; // Reset quality with smaller dimensions
    }
    
    attempts++;
  }

  // Last resort: very aggressive dimension reduction to guarantee under 1MB
  onProgress?.('Applying maximum compression...');
  let finalWidth = Math.round(width * 0.5);
  let finalHeight = Math.round(height * 0.5);
  let finalCompressed = await compressImage(img, finalWidth, finalHeight, 0.6, file.type);
  
  // If still too large, keep reducing
  let extraAttempts = 0;
  while (finalCompressed.size > maxSizeBytes && extraAttempts < 5) {
    finalWidth = Math.round(finalWidth * 0.8);
    finalHeight = Math.round(finalHeight * 0.8);
    finalCompressed = await compressImage(img, finalWidth, finalHeight, 0.5, file.type);
    extraAttempts++;
  }
  
  return {
    file: finalCompressed,
    originalSize,
    finalSize: finalCompressed.size,
    wasResized: true,
    compressionRatio: originalSize / finalCompressed.size
  };
}

/**
 * Load an image from a File object
 */
function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    
    img.src = url;
  });
}

/**
 * Compress an image to a specific size and quality
 */
function compressImage(
  img: HTMLImageElement,
  width: number,
  height: number,
  quality: number,
  originalMimeType: string
): Promise<File> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }
    
    // Use better image smoothing
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Draw image
    ctx.drawImage(img, 0, 0, width, height);
    
    // Determine output format (prefer JPEG for photos, keep PNG for transparency)
    let mimeType = originalMimeType;
    if (originalMimeType === 'image/png' && quality < 0.8) {
      // Convert PNG to JPEG if heavily compressing (unless it has transparency)
      mimeType = 'image/jpeg';
    }
    
    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to compress image'));
          return;
        }
        
        // Create File from Blob
        const file = new File([blob], 'compressed-image', { type: mimeType });
        resolve(file);
      },
      mimeType,
      quality
    );
  });
}

/**
 * Format bytes to human-readable size
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
